import { useState } from 'react';
import { Plus, Eye, EyeOff } from 'lucide-react';
import { FieldCard } from './FieldCard';
import { AddEditFieldModal } from './AddEditFieldModal';
import styles from './JsonFieldBuilder.module.css';

export interface JsonField {
    nombre: string;
    tipo: 'string' | 'number' | 'email' | 'date' | 'boolean';
    requerido: boolean;
    descripcion?: string;
    longitudMaxima?: number;
    mensajeError?: string;
}

export interface JsonFieldBuilderProps {
    value: string; // JSON string
    onChange: (json: string) => void;
    error?: string;
}

export const JsonFieldBuilder = ({ value, onChange, error }: JsonFieldBuilderProps) => {
    const [fields, setFields] = useState<JsonField[]>(() => {
        try {
            return value ? JSON.parse(value) : [];
        } catch {
            return [];
        }
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [showPreview, setShowPreview] = useState(false);

    // Actualizar JSON cuando cambien los campos
    const updateJson = (newFields: JsonField[]) => {
        setFields(newFields);
        onChange(newFields.length > 0 ? JSON.stringify(newFields, null, 2) : '');
    };

    const handleAddField = () => {
        setEditingIndex(null);
        setIsModalOpen(true);
    };

    const handleEditField = (index: number) => {
        setEditingIndex(index);
        setIsModalOpen(true);
    };

    const handleDeleteField = (index: number) => {
        const newFields = fields.filter((_, i) => i !== index);
        updateJson(newFields);
    };

    const handleSaveField = (field: JsonField) => {
        let newFields: JsonField[];

        if (editingIndex !== null) {
            // Editar campo existente
            newFields = fields.map((f, i) => i === editingIndex ? field : f);
        } else {
            // Agregar nuevo campo
            newFields = [...fields, field];
        }

        updateJson(newFields);
        setIsModalOpen(false);
        setEditingIndex(null);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <label className={styles.label}>Campos Personalizados</label>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={handleAddField}
                        title="Agregar campo"
                    >
                        <Plus size={18} strokeWidth={2.5} />
                    </button>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.viewJsonButton}`}
                        onClick={() => setShowPreview(!showPreview)}
                        title={showPreview ? 'Ocultar JSON' : 'Ver JSON'}
                    >
                        {showPreview ? <EyeOff size={18} strokeWidth={2.5} /> : <Eye size={18} strokeWidth={2.5} />}
                    </button>
                </div>
            </div>

            {/* Preview JSON */}
            {showPreview && (
                <div className={styles.preview}>
                    <pre>{value ? JSON.stringify(JSON.parse(value), null, 2) : '[]'}</pre>
                </div>
            )}

            {/* Lista de campos */}
            <div className={styles.fieldsList}>
                {fields.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No hay campos personalizados aún.</p>
                        <p className={styles.emptyHint}>
                            Haz clic en "Agregar Campo" para comenzar.
                        </p>
                    </div>
                ) : (
                    fields.map((field, index) => (
                        <FieldCard
                            key={index}
                            field={field}
                            onEdit={() => handleEditField(index)}
                            onDelete={() => handleDeleteField(index)}
                        />
                    ))
                )}
            </div>



            {/* Error */}
            {error && <div className={styles.error}>{error}</div>}

            {/* Modal agregar/editar */}
            <AddEditFieldModal
                isOpen={isModalOpen}
                field={editingIndex !== null ? fields[editingIndex] : undefined}
                existingFieldNames={fields
                    .filter((_, i) => i !== editingIndex)
                    .map(f => f.nombre)}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingIndex(null);
                }}
                onSave={handleSaveField}
            />
        </div>
    );
};
